import { FormCheckbox, FormDatePicker, FormInput, Heading } from "@/components";
import { Button } from "@/components/ui";
import { LOGIN_ROLES, getBasePath } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { Bookmark, Download } from "lucide-react";
import { FC } from "react";
import { FormProvider } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  EXPECTED_DUE_DATE,
  GENERATED_BY,
  GENERATED_DATE,
  GST_PERCENTAGE,
  INSTALLMENT_AMOUNT,
  INSTALLMENT_AMOUNT_GST,
  INSTALLMENT_AMOUNT_NON_GST,
  INSTALLMENT_NUMBER,
  TDS,
  TOTAL_INVOICE_AMOUNT,
  YEAR,
} from "./consts";
import InstallmentInvoice from "./invoice/InstallmentInvoice";
import PaymetTrackingTable from "./payment/PaymentTrackingTable";
import PdcDetailsTable from "./pdc/PdcDetailsTable";
import useEditInstallmentSchedule from "./useEditInstallmentSchedule";
import { useGetSchoolByIdQuery } from "@/services";

const EditInstallmentSchedule: FC = () => {
  const navigate = useNavigate();
  const { action, id, installmentIndex } = useParams();
  const {
    form,
    requiredFields,
    onSubmit,
    onCancel,
    attachmentRef,
    isLoading,
    pdcData,
    handlePdcCheck,
    isPdcCheckEnabled,
    isPdcLoading,
    donwloadReceipt,
    receiptForm,
    isCRM_HEAD,
  } = useEditInstallmentSchedule({
    schoolId: id || "",
    installmentIndex: installmentIndex || "0",
  });
  const { data: schoolRes, isPending: isSchoolLoading } = useGetSchoolByIdQuery(
    id || "",
    { enabled: Boolean(id) } as any
  );

  const school = (schoolRes as any)?.data || {};
  const { errors } = form.formState;
  const handlePdcUpdate = () => {
    // Refetch PDC data when updated
    // The query will automatically refetch due to React Query's cache invalidation
  };
  const isView = action === "view" && !!id;
  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );
  
    // 👇 backend sends role as array, e.g. ["CRM_HEAD"]
    const roles = loggedInUserData?.role || [];
  
    // Helper to check if user has a given role
    const hasRole = (role: string) => Array.isArray(roles) && roles.includes(role);

  return (
    <div className="p-7">
      {/* Header with breadcrumbs */}
      <Heading
        title={
          <span>
            <span className="text-g-300">School Onboarding</span>
            <span className="mx-3">|</span>
            <span
              className="text-g-300 cursor-pointer"
              onClick={() => {
                const basePath = getBasePath(loggedInUserData?.role);
                navigate(`/${basePath}/school/${id}/edit/`);
              }}
            >
              Edit School {school?.schoolName ? ` (${school?.schoolName})` : ""}
            </span>
            <span className="mx-3">|</span>
            <span>Edit Installment Schedule</span>
          </span>
        }
      />

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          {/* Section 1: Installment Calculation */}
          <div className="bg-white rounded-lg p-6 shadow-sm my-5">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg text-primary">Installment Calculation</h2>
              {!isView && (
                <Button
                  type="submit"
                  variant="default"
                  disabled={isLoading}
                >
                  Save
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <FormInput
                name={YEAR}
                control={form.control}
                label="Year *"
                placeholder="Autopopulated"
                error={errors[YEAR]?.message}
                isRequired={requiredFields.includes("year")}
                disabled
              />
              <FormInput
                name={INSTALLMENT_NUMBER}
                control={form.control}
                label="Installment # "
                placeholder="Enter Installment Number"
                error={errors[INSTALLMENT_NUMBER]?.message}
                isRequired={requiredFields.includes("installmentNumber")}
              />
              <div className="flex items-center pt-6">
                <FormCheckbox
                  name={TDS}
                  control={form.control}
                  label="TDS"
                  error={errors[TDS]?.message}
                  disabled
                />
              </div>
              <FormDatePicker
                name={EXPECTED_DUE_DATE}
                control={form.control}
                label="Expected Due Date*"
                placeholder="Autopopulated"
                error={errors[EXPECTED_DUE_DATE]?.message}
                isRequired={requiredFields.includes("expectedDueDate")}
                disabled
              />
              {/* <FormInput
                name={INSTALLMENT_AMOUNT}
                control={form.control}
                label="Installment Amount"
                prefix="₹"
                error={errors[INSTALLMENT_AMOUNT]?.message}
                isRequired={requiredFields.includes("installmentAmount")}
                disabled
              /> */}
              <FormInput
                name={GST_PERCENTAGE}
                control={form.control}
                label="GST notes"
                placeholder="Enter "
                error={errors[GST_PERCENTAGE]?.message} 
              />
              {/* <FormInput
                name={INSTALLMENT_AMOUNT_NON_GST}
                control={form.control}
                label="Installment Amount (Non-GST)"
                prefix="₹"
                placeholder="Autopopulated"
                error={errors[INSTALLMENT_AMOUNT_NON_GST]?.message}
                disabled
              />
              <FormInput
                name={INSTALLMENT_AMOUNT_GST}
                control={form.control}
                label="Installment Amount (GST)"
                prefix="₹"
                placeholder="Autopopulated"
                error={errors[INSTALLMENT_AMOUNT_GST]?.message}
                disabled
              /> */}
              <FormInput
                name={TOTAL_INVOICE_AMOUNT}
                control={form.control}
                label="Total Invoice Amount"
                prefix="₹"
                placeholder="Autopopulated"
                error={errors[TOTAL_INVOICE_AMOUNT]?.message}
                {...(hasRole(LOGIN_ROLES.INVENTORY_TEAM) ||
                  hasRole(LOGIN_ROLES.SALES_OPS) ||
                  hasRole(LOGIN_ROLES.SUPER_ADMIN)) ? {} : {disabled: true}}
              />
            </div>
          </div>
        </form>
      </FormProvider>
      {/* PDC Details Table */}
      <PdcDetailsTable
        data={pdcData?.data || []}
        isLoading={isPdcLoading}
        onPdcCheck={handlePdcCheck}
        isPdcCheckEnabled={isPdcCheckEnabled}
        installmentId={installmentIndex || ""}
        onPdcUpdate={handlePdcUpdate}
      />
      <div className="py-5">
        <InstallmentInvoice />
      </div>
      <PaymetTrackingTable
        data={pdcData?.data || []}
        isLoading={isPdcLoading}
        onPdcCheck={handlePdcCheck}
        isPdcCheckEnabled={isPdcCheckEnabled}
        installmentId={installmentIndex || ""}
        onPdcUpdate={handlePdcUpdate}
      />
      <div className="py-5">
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-primary">
              Receipt Management
            </h2>
            <div className="flex items-center space-x-2">
              <Button
                type="button"
                variant="link"
                className="text-blue-600"
                onClick={() => donwloadReceipt(installmentIndex || "")}
              >
                <Download className="w-4 h-4 mr-2" />
                Download Receipt
              </Button>
            </div>
          </div>
          <div>
            <form>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <FormDatePicker
                  name={GENERATED_DATE}
                  control={receiptForm.control}
                  label="Generated Date"
                  placeholder="Autopopulated"
                  disabled="true"
                />
                <FormInput
                  name={GENERATED_BY}
                  control={receiptForm.control}
                  label="Generated By"
                  placeholder="Autopopulated"
                  disabled="true"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditInstallmentSchedule;
